// **The three waiting states** (`client-rounds-phase`'s "Waiting states name their subject and
// carry the phase clock" requirement; sketch `prototype/sketches/rounds-client/waiting-states.html`).
//
// A turn-based session spends most of its time with nothing to act on, and the spec is explicit
// that none of those stretches may render as a bare spinner, an error, or with failure chrome —
// each names who or what is being waited on. This module is the **selection**, not the rendering:
// which of the three states (or none) applies, given the phase snapshot and whether the player has
// produced a candidate this round. `SandboxScreen.tsx` draws the cards from it; nothing here
// touches the DOM, a clock or a store.
//
// **Why "has any candidate" rather than a count.** Design D5 calls this "the coordinator's
// candidate list", but `sandbox-coordinator.ts`'s state tracks only the *current* run and world,
// never a history — and it does not need to, because a round's sandbox starts fresh (the
// round-boundary rebind clears candidates, D7) and `SandboxState.run`/`SandboxState.world` never
// revert to their initial values once a run has landed, even if the player goes on to select a
// different, never-run version. So "has the player run anything this round" is exactly
// `run.kind !== 'none' || world.kind === 'loaded'` — a boolean the screen already has for free —
// and this module asks for that boolean rather than a list it would have nowhere honest to get.
//
// **Priority, not overlap.** A round moving straight from "waiting on others" to "simulating" is
// the ordinary case, and the states are mutually exclusive by construction: `simulating` wins
// outright (the editing phase has closed, so nothing about candidates or submissions is relevant
// any more), and within `editing`, "nothing run yet" beats a submission check — a player cannot
// have a recorded submission without ever having produced a candidate.

import type { PhaseSurface, RoundsPhaseSnapshot } from '../../app/rounds/rounds-phase';

/**
 * **Whether the round's graded result is readable** — the one place the two phases that answer yes
 * are named, so the card's waiting arm and the composition root's derivation of the result it draws
 * cannot disagree about when there is one.
 *
 * `Standings` is included, and it is the case the shipped rule missed (E2E finding M-5). `Review` is
 * host-advanced on a configured backstop, so a one-round tournament sits in `Standings` for most of
 * the battle-royale playback — where the round is graded *more* definitively than in `Review`, and
 * where the sandbox screen was rendering nothing but its "closed for this round" gate notice.
 */
export function roundResultReadable(surface: PhaseSurface): boolean {
  return surface === 'reviewing' || surface === 'standings';
}

export interface WaitingStateInput {
  readonly phase: RoundsPhaseSnapshot;
  /** Whether the player has produced at least one candidate (a run) in this round's sandbox — see
   * the module header for where this boolean comes from. */
  readonly hasAnyCandidate: boolean;
}

/**
 * `'none'` is not a fourth waiting state — it is "nothing to name", and it is what the screen
 * reads to render no card at all. Every other arm carries exactly what its card needs to say and
 * nothing else: there is no `tone`, no `severity` and no `error` field anywhere in this type, so a
 * state drawn from it cannot carry failure chrome by construction.
 */
export type WaitingState =
  | { readonly kind: 'none' }
  /** The join window: no deadline, no submission, nothing to name but the player's own arrival
   * (`client-rounds-phase`, "waiting states name their subject and carry the phase clock"). Named
   * fields rather than a `phase` re-read, so the card renders from this value alone. */
  | { readonly kind: 'awaiting-start'; readonly displayName: string; readonly joinOrdinal: number }
  | { readonly kind: 'nothing-run-yet' }
  | { readonly kind: 'waiting-on-others'; readonly outstanding: number }
  | { readonly kind: 'simulating'; readonly round: number | null }
  /** The round's result is readable — `Review`, and the `Standings` that follows it: a waiting
   * surface too, whose result card owns its data and its explicit entry action. */
  | { readonly kind: 'reviewing'; readonly round: number | null };

export function waitingStateFor({ phase, hasAnyCandidate }: WaitingStateInput): WaitingState {
  if (!phase.known) {
    return { kind: 'none' };
  }
  if (phase.surface === 'gathering') {
    return {
      kind: 'awaiting-start',
      displayName: phase.displayName ?? '',
      joinOrdinal: phase.joinOrdinal ?? 0,
    };
  }
  if (phase.surface === 'simulating') {
    return { kind: 'simulating', round: phase.round };
  }
  if (roundResultReadable(phase.surface)) {
    return { kind: 'reviewing', round: phase.round };
  }
  if (phase.surface !== 'editing') {
    // Cancelling/cancelled and an unrecognized phase all have their own surfaces or degrade
    // elsewhere (`client-rounds-phase`'s unknown-phase requirement).
    return { kind: 'none' };
  }
  if (!hasAnyCandidate) {
    return { kind: 'nothing-run-yet' };
  }
  if (phase.submission !== null && phase.outstanding !== null && phase.outstanding > 0) {
    return { kind: 'waiting-on-others', outstanding: phase.outstanding };
  }
  return { kind: 'none' };
}
